import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { ThemeContext } from 'src/store/ContextStore';
import {
  NOTE_TYPE_COLOURS,
  NOTE_TYPE_LABEL_COLOURS
} from 'src/components/Constants';
import {
  getRecordTitle,
  readRecordDate,
  riskLabelText
} from './forms/formService';
import {
  PEDIGREE_RECORD_SURFACES,
  PREG_ACCENT,
  recordCardTint,
  recordCardTintBorder
} from './recordCardStyles';
import { fetusEventSubjectLabel } from '../../../pedigree-tool/pedigree/fetusEventSubject';
import useDateFormatter, {
  formatDateForDisplay
} from 'src/hooks/useDateFormatter';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import AttachmentList from 'src/components/UI/input/AttachmentList';
import { appointmentPeopleRows } from './appointmentAttendees';
import { formatTimeOfDay } from './forms/appointmentStatusRules';
import { recordAddressFor } from './recordAddress';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { triageSummary } from '../triage/triageText';
import { measurementSummary } from '../qrisk3/measurementsText';
import { qrisk3Summary } from '../qrisk3/qrisk3Text';

// Reproductive display labels (Bennett). Kept in sync with the pregnancy editor's
// OUTCOME_LABELS / conceived-with options; small + low-churn, so inlined here rather
// than importing the whole editor module (axios/dialog) into the record card.
const REPRO_OUTCOME_LABEL = {
  ongoing: 'Ongoing',
  sab: 'Miscarriage (SAB)',
  ectopic: 'Ectopic (ECT)',
  top: 'Termination (TOP)',
  stillbirth: 'Stillbirth (SB)',
  liveBirth: 'Live birth'
};
const REPRO_CONCEIVED_LABEL = {
  partner: 'Partner',
  spermDonor: 'Sperm donor',
  eggDonor: 'Egg donor',
  embryoDonor: 'Donated embryo',
  partnerAndDonor: 'Partner + donor'
};

const BodyRow = ({ label, value, multiline }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1,
      py: 0.4,
      alignItems: multiline ? 'flex-start' : 'baseline'
    }}
  >
    <Typography
      variant="body1"
      sx={{
        fontWeight: 'bold',
        minWidth: 130,
        flexShrink: 0,
        pt: multiline ? 0.2 : 0
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
        wordBreak: 'break-word'
      }}
    >
      {value}
    </Typography>
  </Box>
);

const RecordBody = ({
  record,
  staffMemberOptions,
  dateFormat,
  carrier,
  personLabel
}) => {
  const t = useAutoTranslation();

  // Every date in a record's body is picked in a form (a local-anchored
  // instant) AND written by the spreadsheet importers (UTC midnight), so it is
  // read by toLocalDateFromPickerOrImport, as the record forms read it, then
  // shown in the configured format. `moment(d)` read each by its local parts,
  // so an imported date showed a day early at any negative offset.
  const fmt = (d) =>
    formatDateForDisplay(toLocalDateFromPickerOrImport(d), dateFormat);
  const findStaff = (id) =>
    staffMemberOptions?.find((s) => s.value === id)?.label || id;

  switch (record.noteType) {
    case 'Appointment':
      return (
        <Box>
          {record.appointmentDate && (
            <BodyRow label={t('Date')} value={fmt(record.appointmentDate)} />
          )}
          {record.appointmentTime && (
            <BodyRow
              label={t('Time')}
              value={
                formatTimeOfDay(record.appointmentTime) ||
                String(record.appointmentTime)
              }
            />
          )}
          {record.clinicName && (
            <BodyRow label={t('Clinic')} value={record.clinicName} />
          )}
          {record.appointmentType && (
            <BodyRow label={t('Type')} value={record.appointmentType} />
          )}
          {record.appointmentStatus && (
            <BodyRow label={t('Status')} value={record.appointmentStatus} />
          )}
          {record.statusReason && (
            <BodyRow label={t('Reason')} value={record.statusReason} />
          )}
          {record.location && (
            <BodyRow label={t('Location')} value={record.location} />
          )}
          {record.staffMember?.length > 0 && (
            <BodyRow
              label={t('Staff')}
              value={record.staffMember.map(findStaff).join(', ')}
            />
          )}
          {appointmentPeopleRows(
            record,
            recordAddressFor(carrier),
            personLabel,
            t
          ).map((row) => (
            <BodyRow key={row.label} label={t(row.label)} value={row.value} />
          ))}
          {record.appointmentNotes && (
            <BodyRow
              label={t('Notes')}
              value={record.appointmentNotes}
              multiline
            />
          )}
        </Box>
      );

    case 'Referral':
      return (
        <Box>
          {record.referralDate && (
            <BodyRow label={t('Date')} value={fmt(record.referralDate)} />
          )}
          {record.referralType && (
            <BodyRow label={t('Type')} value={record.referralType} />
          )}
          {record.referralTo && (
            <BodyRow label={t('Referred To')} value={record.referralTo} />
          )}
          {record.referredBy && (
            <BodyRow label={t('Referred By')} value={record.referredBy} />
          )}
          {record.referralStatus && (
            <BodyRow label={t('Status')} value={record.referralStatus} />
          )}
          {record.referralTriage && (
            <BodyRow label={t('Triage')} value={record.referralTriage} />
          )}
          {record.referralReason && (
            <BodyRow
              label={t('Reason')}
              value={record.referralReason}
              multiline
            />
          )}
          {record.referralNotes && (
            <BodyRow
              label={t('Notes')}
              value={record.referralNotes}
              multiline
            />
          )}
          {record.dateClosed && (
            <BodyRow label={t('Date Closed')} value={fmt(record.dateClosed)} />
          )}
          {record.reasonForClosure && (
            <BodyRow
              label={t('Reason for Closure')}
              value={record.reasonForClosure}
              multiline
            />
          )}
        </Box>
      );

    case 'Test':
      return (
        <Box>
          {record.testName && (
            <BodyRow label={t('Test Name')} value={record.testName} />
          )}
          {record.testType && (
            <BodyRow label={t('Type')} value={record.testType} />
          )}
          {record.dateRequested && (
            <BodyRow
              label={t('Date Requested')}
              value={fmt(record.dateRequested)}
            />
          )}
          {record.dateResultDue && (
            <BodyRow label={t('Date Due')} value={fmt(record.dateResultDue)} />
          )}
          {record.dateResultReceived && (
            <BodyRow
              label={t('Date Received')}
              value={fmt(record.dateResultReceived)}
            />
          )}
          {record.lab && <BodyRow label={t('Lab')} value={record.lab} />}
          {record.ageWhenTested != null && (
            <BodyRow
              label={t('Age When Tested')}
              value={record.ageWhenTested}
            />
          )}
          {record.testResult && (
            <BodyRow label={t('Result')} value={record.testResult} multiline />
          )}
          {record.testNotes && (
            <BodyRow label={t('Notes')} value={record.testNotes} multiline />
          )}
        </Box>
      );

    case 'Clinical Note':
      return (
        <Box>
          {record.clinicalNoteDate && (
            <BodyRow label={t('Date')} value={fmt(record.clinicalNoteDate)} />
          )}
          {record.clinician && (
            <BodyRow label={t('Clinician')} value={record.clinician} />
          )}
          {record.note && (
            <BodyRow label={t('Note')} value={record.note} multiline />
          )}
        </Box>
      );

    case 'Sample':
      return (
        <Box>
          {record.dateTaken && (
            <BodyRow label={t('Date Taken')} value={fmt(record.dateTaken)} />
          )}
          {record.sampleType && (
            <BodyRow label={t('Type')} value={record.sampleType} />
          )}
          {record.laboratory && (
            <BodyRow label={t('Laboratory')} value={record.laboratory} />
          )}
          {record.sampleStatus && (
            <BodyRow label={t('Status')} value={record.sampleStatus} />
          )}
          {record.statusDate && (
            <BodyRow label={t('Status Date')} value={fmt(record.statusDate)} />
          )}
          {record.sampleHeldBy && (
            <BodyRow label={t('Held By')} value={record.sampleHeldBy} />
          )}
          {record.trackingId && (
            <BodyRow label={t('Tracking ID')} value={record.trackingId} />
          )}
        </Box>
      );

    case 'Treatment':
      return (
        <Box>
          {record.treatmentDate && (
            <BodyRow
              label={t('Start Date')}
              value={fmt(record.treatmentDate)}
            />
          )}
          {record.treatmentEndDate && (
            <BodyRow
              label={t('End Date')}
              value={fmt(record.treatmentEndDate)}
            />
          )}
          {record.treatment && (
            <BodyRow
              label={t('Treatment')}
              value={record.treatment}
              multiline
            />
          )}
          {record.comment && (
            <BodyRow label={t('Comment')} value={record.comment} multiline />
          )}
        </Box>
      );

    case 'Contact':
      return (
        <Box>
          {record.contactDate && (
            <BodyRow label={t('Date')} value={fmt(record.contactDate)} />
          )}
          {record.contactType && (
            <BodyRow label={t('Type')} value={record.contactType} />
          )}
          {record.contactMode && (
            <BodyRow label={t('Mode')} value={record.contactMode} />
          )}
          {record.contactStatus && (
            <BodyRow label={t('Status')} value={record.contactStatus} />
          )}
          {record.reasonForContact && (
            <BodyRow
              label={t('Reason')}
              value={record.reasonForContact}
              multiline
            />
          )}
          {record.staffMember?.length > 0 && (
            <BodyRow
              label={t('Staff')}
              value={record.staffMember.map(findStaff).join(', ')}
            />
          )}
          {record.contactNotes && (
            <BodyRow label={t('Notes')} value={record.contactNotes} multiline />
          )}
        </Box>
      );

    case 'Consent':
      return (
        <Box>
          {(record.consentDate || record.consentRequestDate) && (
            <BodyRow
              label={t('Date')}
              value={fmt(record.consentDate || record.consentRequestDate)}
            />
          )}
          {record.consentRequestDate && record.consentDate && (
            <BodyRow
              label={t('Request Date')}
              value={fmt(record.consentRequestDate)}
            />
          )}
          {record.consentType && (
            <BodyRow label={t('Type')} value={record.consentType} />
          )}
          {record.response && (
            <BodyRow label={t('Response')} value={record.response} />
          )}
          {record.consentGivenBy && (
            <BodyRow label={t('Given By')} value={record.consentGivenBy} />
          )}
          {record.expiryDate && (
            <BodyRow label={t('Expiry')} value={fmt(record.expiryDate)} />
          )}
          {record.consentNotes && (
            <BodyRow label={t('Notes')} value={record.consentNotes} multiline />
          )}
        </Box>
      );

    case 'Address':
      return (
        <Box>
          {record.addressLine1 && (
            <BodyRow label={t('Line 1')} value={record.addressLine1} />
          )}
          {record.addressLine2 && (
            <BodyRow label={t('Line 2')} value={record.addressLine2} />
          )}
          {(record.town || record.city) && (
            <BodyRow
              label={t('Town/City')}
              value={record.town || record.city}
            />
          )}
          {record.postCode && (
            <BodyRow label={t('Postcode')} value={record.postCode} />
          )}
          {record.country && (
            <BodyRow label={t('Country')} value={record.country} />
          )}
          {record.primaryAddress && (
            <BodyRow label={t('Primary')} value={t('Yes')} />
          )}
          {record.correspondenceAddress && (
            <BodyRow label={t('Correspondence')} value={t('Yes')} />
          )}
        </Box>
      );

    case 'Tissue Pathology':
      return (
        <Box>
          {record.tissueSite && (
            <BodyRow label={t('Tissue Site')} value={record.tissueSite} />
          )}
          {record.morphology && (
            <BodyRow label={t('Morphology')} value={record.morphology} />
          )}
          {record.pathReportDate && (
            <BodyRow
              label={t('Report Date')}
              value={fmt(record.pathReportDate)}
            />
          )}
          {record.staffMember?.length > 0 && (
            <BodyRow
              label={t('Staff')}
              value={record.staffMember.map(findStaff).join(', ')}
            />
          )}
          {record.pathologyDoctor && (
            <BodyRow
              label={t('Pathologist')}
              value={findStaff(record.pathologyDoctor)}
            />
          )}
        </Box>
      );

    case 'Disease':
      return (
        <Box>
          {record.id && <BodyRow label={t('ID')} value={record.id} />}
          {record.age && (
            <BodyRow label={t('Age at diagnosis')} value={record.age} />
          )}
          {record.dateReported && (
            <BodyRow
              label={t('Date reported')}
              value={fmt(record.dateReported)}
            />
          )}
          <BodyRow
            label={t('Clinically confirmed')}
            value={record.clinicallyConfirmed ? t('Yes') : t('No')}
          />
        </Box>
      );

    case 'Phenotype':
      return (
        <Box>
          {record.id && <BodyRow label={t('ID')} value={record.id} />}
          {record.recordedAge && (
            <BodyRow label={t('Age reported')} value={record.recordedAge} />
          )}
          {record.dateRecorded && (
            <BodyRow
              label={t('Date recorded')}
              value={fmt(record.dateRecorded)}
            />
          )}
          <BodyRow
            label={t('Clinically confirmed')}
            value={record.clinicallyConfirmed ? t('Yes') : t('No')}
          />
        </Box>
      );

    case 'Genotype':
      return (
        <Box>
          {record.id && <BodyRow label={t('ID')} value={record.id} />}
          {record.carrierStatus && (
            <BodyRow label={t('Carrier status')} value={record.carrierStatus} />
          )}
          {record.recordedAge && (
            <BodyRow label={t('Age reported')} value={record.recordedAge} />
          )}
          {record.dateRecorded && (
            <BodyRow
              label={t('Date recorded')}
              value={fmt(record.dateRecorded)}
            />
          )}
          <BodyRow
            label={t('Clinically confirmed')}
            value={record.clinicallyConfirmed ? t('Yes') : t('No')}
          />
        </Box>
      );

    case 'Pregnancy':
      return (
        <Box>
          <BodyRow
            label={t('Outcome')}
            value={REPRO_OUTCOME_LABEL[record.outcome] || record.outcome}
          />
          {record.gestationalWeeks != null &&
            record.gestationalWeeks !== '' && (
              <BodyRow
                label={t('Gestational age')}
                value={t('{{gestationalWeeks}} weeks', {
                  gestationalWeeks: record.gestationalWeeks
                })}
              />
            )}
          {record.conceivedWith && (
            <BodyRow
              label={t('Conceived with')}
              value={
                REPRO_CONCEIVED_LABEL[record.conceivedWith] ||
                record.conceivedWith
              }
            />
          )}
          {record.fetalSexKnown && record.fetalSex && (
            <BodyRow
              label={t('Fetal sex')}
              value={record.fetalSex === 'male' ? t('Male') : t('Female')}
            />
          )}
          {record.karyotype && (
            <BodyRow
              label={t('Karyotype / finding')}
              value={record.karyotype}
            />
          )}
          {record.comments && (
            <BodyRow label={t('Note')} value={record.comments} multiline />
          )}
          {record.hidden && (
            <BodyRow
              label={t('Visibility')}
              value={t('Hidden from pedigree')}
            />
          )}
          {/* A MULTIPLE lists every fetus. The rows above mirror only the first one, so without this a
              twin pregnancy read as a singleton. Present only when there are two or more. */}
          {(record.fetusRows || []).map((f, i) => (
            <BodyRow
              key={i}
              label={f.label}
              value={[
                REPRO_OUTCOME_LABEL[f.outcome] || f.outcome,
                f.gender === 'male'
                  ? 'Male'
                  : f.gender === 'female'
                  ? 'Female'
                  : null,
                f.karyotype,
                f.diagnoses?.length ? f.diagnoses.join(', ') : null,
                f.born ? 'on the pedigree as a child' : null,
                f.hidden ? 'hidden from pedigree' : null
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          ))}
        </Box>
      );

    case 'Risk Score': {
      let summary = '';
      if (record.recordType === 'gail' && record.gailRiskScore != null) {
        summary = t('Risk score: {{gailRiskScore}}', {
          gailRiskScore: record.gailRiskScore
        });
      } else if (record.recordType === 'canrisk') {
        try {
          const pr = record.canriskResult?.pedigree_result?.[0];
          const tenYr =
            pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
          const lifetime =
            pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
          if (tenYr != null && lifetime != null) {
            summary = t('10yr: {{tenYr}}% | Lifetime: {{lifetime}}%', {
              tenYr,
              lifetime
            });
          }
        } catch (_) {
          /* ignore */
        }
        if (!summary) {
          summary = record.canriskResult
            ? t('Scores calculated')
            : t('Pedigree downloaded');
        }
      } else if (record.recordType === 'prs') {
        const pr = record.prsResult;
        const label = pr?.trait || pr?.pgsId;
        const parts = [
          pr?.zscore != null ? `z=${pr.zscore}` : null,
          pr?.percentile != null ? `${pr.percentile}th percentile` : null
        ].filter(Boolean);
        summary = label ? [label, ...parts].join(' — ') : t('PRS record saved');
      } else if (record.recordType === 'questionnaire') {
        summary = t('Submitted from family history questionnaire');
      } else if (record.recordType === 'triage') {
        summary = triageSummary(record.triageResult, t);
      } else if (record.recordType === 'measurements') {
        summary = measurementSummary(record.measurements, t);
      } else if (record.recordType === 'qrisk3') {
        summary = qrisk3Summary(record.qrisk3Result, t);
      }
      return (
        <Box>
          <BodyRow
            label={t('Type')}
            value={riskLabelText(record.riskLabel, t)}
          />
          {summary && <BodyRow label={t('Result')} value={summary} />}
        </Box>
      );
    }

    default:
      return null;
  }
};

const RecordCard = ({
  record,
  isExpanded,
  isFocused,
  onToggle,
  onFocus,
  cardRef,
  dateFormat,
  staffMemberOptions,
  surfaces = PEDIGREE_RECORD_SURFACES,
  // The member this card is being shown under, used only to resolve a PRENATAL event's subject
  // (decision 21). Optional — surfaces that do not have it simply show no subject chip.
  carrier,
  personLabel,
  onOpen
}) => {
  const t = useAutoTranslation();

  const { theme: cardTheme } = React.useContext(ThemeContext);
  const accentColor = cardTheme?.accent || '#1976d2';
  // Same reason as NoteTile: the subject chip carries a date, so it follows the
  // configured format rather than a hardcoded locale (TGCLOUD-1707).
  const fmtDate = useDateFormatter();
  // The browser hands its resolved format down; a card mounted without one
  // follows the configured format too, not moment's ISO default.
  const shownDateFormat = dateFormat || cardTheme?.dateFormat;

  // Whose event is this? A prenatal event is stored against the MOTHER, so without this an
  // amniocentesis or a fetal karyotype sits in her record reading as something done to HER. The label
  // is computed by the shared helper so this surface and the fetus-side one cannot disagree.
  const subject = fetusEventSubjectLabel(record, carrier, fmtDate, t);

  const title = getRecordTitle(record, t);
  // The day the record is dated, read by how that date was written
  // (readRecordDate).
  const day = readRecordDate(record);
  const typeColor = NOTE_TYPE_COLOURS[record.noteType] || '#888';
  const labelColor = surfaces
    ? NOTE_TYPE_LABEL_COLOURS[record.noteType] || typeColor
    : typeColor;

  return (
    <Box
      ref={cardRef}
      role="listitem"
      aria-expanded={isExpanded}
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: isFocused
          ? accentColor
          : surfaces
          ? recordCardTintBorder(typeColor)
          : '#e0e0e0',
        borderLeft: `3px solid ${typeColor}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: surfaces ? recordCardTint(typeColor) : '#fff',
        transition: surfaces
          ? 'border-color 0.15s, transform .15s ease, box-shadow .15s ease'
          : 'border-color 0.15s',
        ...(surfaces && {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(31, 42, 51, .13)'
          }
        })
      }}
    >
      <Box
        onClick={(e) => {
          onFocus();
          onToggle(record._id, e);
        }}
        sx={{
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          userSelect: 'none'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: labelColor,
              lineHeight: 1
            }}
          >
            {t(record.noteType)}
          </Typography>
          {subject && (
            <Chip
              size="small"
              label={subject}
              sx={{
                height: 18,
                ml: 1,
                fontSize: '10px',
                fontWeight: 600,
                // Reuses the reproductive accent so a prenatal event reads as belonging to the
                // Reproductive surfaces rather than looking like another note type.
                color: PREG_ACCENT,
                backgroundColor: recordCardTint(PREG_ACCENT),
                border: `1px solid ${recordCardTintBorder(PREG_ACCENT)}`
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {day && (
              <Typography
                variant="body1"
                sx={{ flexShrink: 0, fontWeight: 700, mr: '4px' }}
              >
                {formatDateForDisplay(day, shownDateFormat)}
              </Typography>
            )}
            {onOpen && (
              <Tooltip title={t('Edit record')}>
                <IconButton
                  size="small"
                  tabIndex={-1}
                  aria-label={t('Edit {{noteType}} record', {
                    noteType: t(record.noteType)
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFocus();
                    onOpen(record);
                  }}
                  sx={{ p: '4px', flexShrink: 0 }}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              tabIndex={-1}
              aria-label={
                isExpanded ? t('Collapse record') : t('Expand record')
              }
              sx={{ p: '4px', flexShrink: 0 }}
            >
              {isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {title}
          </Typography>
          {record.clinicallyConfirmed && (
            <CheckCircleOutlineIcon
              sx={{ color: 'green', fontSize: 18, flexShrink: 0 }}
            />
          )}
        </Box>
      </Box>

      <Collapse in={isExpanded} unmountOnExit>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <RecordBody
            record={record}
            staffMemberOptions={staffMemberOptions}
            dateFormat={shownDateFormat}
            carrier={carrier}
            personLabel={personLabel}
          />
          {record.fileAttachments?.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary' }}
              >
                {t('Attachments')}
              </Typography>
              {/* Openable rows, as in the record form — these were truncated,
                  unclickable chips (TGCLOUD-1850). Links resolve when the card
                  is expanded (this body mounts only then), so a list of
                  collapsed cards costs no requests. */}
              <Box sx={{ mt: 0.5 }}>
                <AttachmentList
                  names={record.fileAttachments}
                  familyId={record.familyMemberId}
                  readOnly
                  dense
                />
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RecordCard;
